import React, { useEffect, useMemo, useRef, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useCmsList } from "../hooks";

interface ICmsSelectSearch {
  form: UseFormReturn<any, any>;
  idName?: string;
  nameName?: string;
  label?: string;
  placeholder?: string;
}

export const CmsSelectSearch: React.FC<ICmsSelectSearch> = ({
  form,
  idName = "cmsId",
  nameName = "cmsName",
  label = "CMS",
  placeholder = "Rechercher ou créer un CMS...",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpened, setIsOpened] = useState(false);
  const [search, setSearch] = useState("");

  const { data } = useCmsList();

  const cmsId = form.watch(idName);

  const selectedName = useMemo(() => {
    if (!cmsId) return "";
    return data?.cmsList?.results?.find((cms) => cms.id === +cmsId)?.name ?? "";
  }, [cmsId, data]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpened(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const clearSelection = () => {
    form.setValue(idName, null);
    form.setValue(nameName, "");
    setSearch("");
  };

  const results = (data?.cmsList?.results ?? []).filter((cms) =>
    search ? cms.name.toLowerCase().includes(search.toLowerCase()) : true
  );

  const hasExactMatch = results.some((cms) => cms.name.toLowerCase() === search.toLowerCase());

  if (cmsId && selectedName) {
    return (
      <div className="flex flex-col w-full">
        <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">{label}</label>
        <div className="flex items-center justify-between px-3 py-2 border border-gray-300 dark:border-zinc-700 rounded-md text-sm bg-gray-50 dark:bg-zinc-900 text-gray-900 dark:text-zinc-100">
          <span className="truncate">{selectedName}</span>
          <button
            type="button"
            onClick={clearSelection}
            className="text-gray-400 dark:text-zinc-500 hover:text-gray-600 dark:hover:text-zinc-300 ml-2"
          >
            ✕
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full relative" ref={containerRef}>
      <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">{label}</label>
      <input
        className="w-full px-3 py-2 border rounded-md text-sm bg-white dark:bg-surface-dark-alt text-gray-900 dark:text-zinc-100 border-gray-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-primary-500 focus:ring-primary-500 transition-colors duration-200"
        placeholder={placeholder}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          form.setValue(nameName, e.target.value);
          setIsOpened(true);
        }}
        onFocus={() => setIsOpened(true)}
        autoComplete="off"
      />

      {isOpened && search.length > 0 && (
        <div className="absolute z-10 top-full mt-1 w-full bg-white dark:bg-surface-dark-alt border border-gray-200 dark:border-zinc-800 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {results.map((cms) => (
            <div
              key={`cms-option-${cms.id}`}
              className="px-3 py-2 text-sm text-gray-900 dark:text-zinc-100 cursor-pointer hover:bg-gray-50 dark:hover:bg-zinc-800"
              onClick={() => {
                form.setValue(idName, cms.id);
                form.setValue(nameName, "");
                setSearch("");
                setIsOpened(false);
              }}
            >
              {cms.name}
            </div>
          ))}
          {!hasExactMatch && (
            <div
              className="px-3 py-2 text-sm text-primary-600 cursor-pointer hover:bg-gray-50 dark:hover:bg-zinc-800"
              onClick={() => {
                form.setValue(idName, null);
                form.setValue(nameName, search);
                setIsOpened(false);
              }}
            >
              + Créer "{search}"
            </div>
          )}
        </div>
      )}
    </div>
  );
};
